/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import styled from "styled-components";
import { v4 } from "uuid";
import {
  theme,
  Text,
  Spacer,
  Button,
  ModalConfirm,
  Flex,
  Tooltip,
  Box,
} from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { ButtonChevron } from "~/components/CollapseItem/CollapseItem";
import { ContractDurationDates } from "~/components/Contract/ContractDurationDates";
import { InlineEditMultiSelect } from "~/components/InlineEdit/InlineEditMultiSelect";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { Action, customerCategoryList } from "~/constants";
import { DurationType, Contract, SubmitContract } from "~/models";
import { ACTIONS, Can } from "~/routing";
import {
  displayCustomerCategory,
  isValidUrlSchema,
  useClickOutside,
} from "~/tools";

const StyledWrapper = styled.div`
  margin: 0.6rem 0 1.2rem 2.4rem;
`;

export const StyledItemWrapper = styled.div`
  margin-top: 1.2rem;
  min-height: 4.8rem;
`;

const StyledText = styled(Text)`
  margin: 0 0 0.3rem;
`;

const StyledLink = styled.a.attrs({
  target: "_blank",
  rel: "noreferrer",
})`
  font-size: ${theme.fontSizes.md};
  margin: 0;
`;

type ContractProps = {
  contract: Contract;
  onSubmit: (values: SubmitContract, type: string) => Promise<void>;
  canEdit?: boolean;
};

export type ContractLoadingType = {
  label: boolean;
  link: boolean;
  durationType: boolean;
  categories: boolean;
  ends: boolean;
  signed: boolean;
  starts: boolean;
};

export type ContractEditModeType = {
  label: boolean;
  link: boolean;
  durationType: boolean;
  categories: boolean;
  ends: boolean;
  signed: boolean;
  starts: boolean;
};

export const contractLoadingInitialState = {
  label: false,
  link: false,
  durationType: false,
  categories: false,
  ends: false,
  signed: false,
  starts: false,
};

export const contractEditModeInitialState = {
  label: false,
  link: false,
  durationType: false,
  categories: false,
  ends: false,
  signed: false,
  starts: false,
};

function ContractItem({ contract, onSubmit, canEdit }: ContractProps) {
  const [isLoading, setIsLoading] = React.useState<ContractLoadingType>(
    contractLoadingInitialState
  );

  const [isEditMode, setIsEditMode] = React.useState<ContractEditModeType>(
    contractEditModeInitialState
  );

  const [isRemoveMode, setIsRemoveMode] = React.useState<boolean>(false);

  const onRemove = async () => {
    setIsRemoveMode(false);
    const organizeValues: SubmitContract = {
      ...contract,
    };
    await onSubmit(organizeValues, Action.REMOVE);
  };
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

  const onEdit = async (values: SubmitContract) => {
    await onSubmit(values, Action.EDIT);
  };

  const wrapperRef = React.useRef(null);

  useClickOutside(
    wrapperRef,
    () => {
      setIsRemoveMode(false);
    },
    isRemoveMode
  );

  function Duration() {
    if (contract.durationType === DurationType.INDEFINITE) {
      return (
        <Text size="sm" tag="span" color={theme.color.text.info}>
          Indefinite
        </Text>
      );
    }
    if (contract.expired) {
      return (
        <Text size="sm" tag="span" color={theme.color.text.error}>
          Ended
        </Text>
      );
    }

    return (
      <Text size="sm" tag="span" color={theme.color.text.success}>
        Active
      </Text>
    );
  }

  function RemoveModal() {
    return (
      <ModalConfirm
        contentLabel={`Delete ${contract.label} contract`}
        isOpen={isRemoveMode}
        confirm={onRemove}
        onClose={() => setIsRemoveMode(false)}
        actionLabel="Delete"
      >
        Do you really want to delete {contract.label} contract?
      </ModalConfirm>
    );
  }

  return (
    <Box boxStyle="lightGrey" spacing="spacing02">
      <Row>
        <Col xs={2} sm={4} md={4} lg={7}>
          <FlexContainer>
            <ButtonChevron
              name="chevronDown"
              onClick={() => setIsExpanded(!isExpanded)}
              isChildren
              isExpanded={isExpanded}
            />
            <FlexContainer
              align="flex-start"
              justify="center"
              direction="column"
            >
              <Text size="lg" weight="medium" tag="div">
                {contract.label}
              </Text>
              <Duration />
            </FlexContainer>
          </FlexContainer>
        </Col>
        <Col xs={3} sm={4} md={4} lg={5}>
          <FlexContainer grow justify="flex-end">
            <Flex justifyContent="flex-end">
              <div
                css={{
                  width: "13rem",
                  paddingLeft: theme.spacing.spacing04,
                  borderLeft: `1px solid ${theme.color.border.border01}`,
                }}
              >
                <Text
                  isTitle
                  size="sm"
                  tag="div"
                  color={theme.color.text.text02}
                  mb={theme.spacing.spacing01}
                >
                  Customer Category:
                </Text>
                <Flex>
                  {contract.categories && contract.categories.length !== 0 ? (
                    contract.categories.map((el, i) => (
                      <Text
                        key={v4()}
                        m="0"
                        mr={
                          contract.categories?.length !== i + 1 ? "0.5rem" : "0"
                        }
                      >
                        {displayCustomerCategory(el)}
                      </Text>
                    ))
                  ) : (
                    <Text size="lg" weight="medium" tag="div">
                      -
                    </Text>
                  )}
                </Flex>
              </div>
              <div
                css={{
                  width: "13rem",
                  paddingLeft: theme.spacing.spacing04,
                  borderLeft: `1px solid ${theme.color.border.border01}`,
                }}
              >
                <Text
                  isTitle
                  size="sm"
                  tag="div"
                  color={theme.color.text.text02}
                  mb={theme.spacing.spacing01}
                >
                  Contract Link:
                </Text>
                {contract.link ? (
                  <Tooltip
                    status="notification"
                    caption={contract.link.slice(0, 30)}
                  >
                    <StyledLink href={contract.link}>Link</StyledLink>
                  </Tooltip>
                ) : (
                  <Text size="lg" weight="medium" tag="div">
                    -
                  </Text>
                )}
              </div>
            </Flex>
          </FlexContainer>
        </Col>
      </Row>
      {isExpanded && (
        <>
          <StyledWrapper>
            <Spacer height="1rem" />
            <Row>
              <Col xs={2} sm={4} md={4} lg={7}>
                <StyledItemWrapper>
                  <StyledText size="sm" color={theme.color.text.text02}>
                    Label
                  </StyledText>
                  <InlineEditText
                    name="label"
                    defaultValue={contract.label}
                    required
                    onSubmit={(label) =>
                      onSubmit({ ...contract, label }, Action.EDIT)
                    }
                    canEdit={canEdit}
                  />
                </StyledItemWrapper>

                <StyledItemWrapper>
                  <StyledText size="sm" color={theme.color.text.text02}>
                    Customer Category
                  </StyledText>
                  <InlineEditMultiSelect
                    options={customerCategoryList}
                    defaultValue={contract.categories || []}
                    onSubmit={(val) =>
                      onSubmit({ ...contract, categories: val }, Action.EDIT)
                    }
                    canEdit={canEdit}
                  />
                </StyledItemWrapper>
              </Col>

              <Col xs={3} sm={4} md={4} lg={5}>
                <StyledItemWrapper>
                  <StyledText size="sm" color={theme.color.text.text02}>
                    Dates
                  </StyledText>
                  <ContractDurationDates
                    item={contract}
                    onSubmit={onEdit}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    isEditMode={isEditMode}
                    setIsEditMode={setIsEditMode}
                    canEdit={canEdit}
                  />
                </StyledItemWrapper>
              </Col>
            </Row>

            <Row>
              <Col>
                <StyledItemWrapper>
                  <StyledText
                    size="sm"
                    color={theme.color.text.text02}
                    mb="0.3rem"
                  >
                    Contract Link
                  </StyledText>
                  <InlineEditText
                    name="link"
                    defaultValue={contract.link}
                    validation={{
                      validate: (link: string) =>
                        isValidUrlSchema.safeParse(link).success ||
                        "This URL is invalid",
                    }}
                    onSubmit={(link) =>
                      onSubmit({ ...contract, link }, Action.EDIT)
                    }
                    canEdit={canEdit}
                  />
                </StyledItemWrapper>
              </Col>
            </Row>

            <Can action={ACTIONS.DELETE_CUSTOMER_CONTRACT}>
              <FlexContainer justify="flex-end">
                <Button severity="high" onClick={() => setIsRemoveMode(true)}>
                  Delete
                </Button>
              </FlexContainer>
            </Can>
          </StyledWrapper>

          <RemoveModal />
        </>
      )}
    </Box>
  );
}

export { ContractItem };
